import { Box, Button, Checkbox, Divider, Flex, Heading, HStack, Icon, Text, VStack, useToast } from "@chakra-ui/react";
import { FiMail, FiCheckCircle, FiUser } from "react-icons/fi";
import { CustomInput, CustomTooltip } from "@lubbezposrednio-frontend/commons";
import { NEW_TAB_WARNING, bindToCommand, convertHtmlToText } from "@lubbezposrednio-frontend/core";
import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { LandingPageViewModel } from "../LandingPageViewModel";

const GLASS = {
  bg: "white",
  border: "1px solid rgba(67,0,152,0.08)",
  boxShadow: "0 16px 40px rgba(67,0,152,0.12)"
} as const;

export const SignUpForVotingForm: React.FC<{ model: LandingPageViewModel }> = observer(({ model }) => {
  const toast = useToast();

  const addEmailCommand = useMemo(() => model.addEmail(toast), [toast, model.addEmail]);

  const Header = (
    <HStack spacing="10px" mb="8px">
      <Flex w="38px" h="38px" borderRadius="12px" bg="rgba(229,31,68,0.1)" align="center" justify="center">
        <Icon as={FiMail} color="accent" boxSize="19px" />
      </Flex>
      <Heading fontSize="21px" fontWeight="800" color="primary">
        Zapisz się do głosowania
      </Heading>
    </HStack>
  );

  // ── Po udanym zapisie ──
  if (model.signedUpDone) {
    return (
      <Box w="100%" {...GLASS} borderRadius="22px" p={{ base: "28px 22px", md: "36px 30px" }} textAlign="center">
        <Flex w="56px" h="56px" mx="auto" borderRadius="16px" bg="rgba(47,206,37,0.14)" align="center" justify="center" mb="16px">
          <Icon as={FiCheckCircle} boxSize="28px" color="#1f9e18" />
        </Flex>
        <Heading fontSize="22px" fontWeight="800" color="primary" mb="8px">
          Zapisano!
        </Heading>
        <Text fontSize="14px" color="gray.500" lineHeight="1.6">
          Jesteś na liście — damy znać, gdy ruszy głosowanie.
        </Text>
      </Box>
    );
  }

  // ── Zalogowany: jeden klik (auto-zgody) ──
  if (model.isLoggedInUser) {
    return (
      <Box w="100%" {...GLASS} borderRadius="22px" p={{ base: "24px", md: "30px" }}>
        {Header}
        <Text fontSize="14px" color="gray.500" mb="20px">
          Jesteś zalogowany — wystarczy jedno kliknięcie.
        </Text>

        <HStack spacing="11px" bg="rgba(67,0,152,0.05)" border="1px solid rgba(67,0,152,0.08)" borderRadius="14px" p="13px 15px" mb="18px">
          <Flex w="34px" h="34px" borderRadius="10px" bg="rgba(67,0,152,0.1)" align="center" justify="center" flexShrink={0}>
            <Icon as={FiUser} color="primary" boxSize="16px" />
          </Flex>
          <Box minW={0}>
            <Text fontSize="12px" color="gray.500">
              Zapisujesz się jako
            </Text>
            <Text fontSize="14px" fontWeight="700" color="primary" noOfLines={1}>
              {model.profileEmail}
            </Text>
          </Box>
        </HStack>

        <Button
          onClick={() => {
            model.checkAllConsents();
            addEmailCommand.execute();
          }}
          isLoading={addEmailCommand.isRunning}
          w="100%"
          bg="accent"
          color="white"
          h="50px"
          borderRadius="13px"
          fontWeight="700"
          fontSize="16px"
          leftIcon={<FiCheckCircle />}
          boxShadow="0 8px 20px rgba(229,31,68,0.24)"
          _hover={{ bg: "#c41a3a" }}
          _active={{ bg: "#a8152f" }}
        >
          Zapisz się jednym kliknięciem
        </Button>

        <Text fontSize="12px" color="gray.400" mt="12px" lineHeight="1.5">
          Klikając, akceptujesz regulamin i zgody wymagane do udziału w tym głosowaniu.
        </Text>
        {!!model.emailError && (
          <Text variant="error" pt="8px" fontSize="13px">
            {model.emailError}
          </Text>
        )}
      </Box>
    );
  }

  // ── Niezalogowany: pełna forma e-mail + zgody ──
  return (
    <Box w="100%" {...GLASS} borderRadius="22px" p={{ base: "24px", md: "30px" }}>
      {Header}
      <Text fontSize="14px" color="gray.500" mb="22px">
        Zostaw e-mail, by wziąć udział. Po zapisie sprawdź skrzynkę i potwierdź adres, klikając link z wiadomości.
      </Text>

      <Text fontSize="13px" fontWeight="700" color="primary" mb="7px">
        Adres e-mail
      </Text>
      <Flex gap="9px" direction={{ base: "column", sm: "row" }}>
        <CustomInput
          value={model.email}
          onChange={model.updateEmail.bind(model)}
          leftIcon={FiMail}
          h="48px"
          flex={1}
          placeholder="twoj@email.pl"
          onKeyDown={(e: React.KeyboardEvent) => (e.key === "Enter" || e.key === "Return") && addEmailCommand.execute()}
        />
        <Button
          {...bindToCommand(addEmailCommand)}
          bg="accent"
          color="white"
          h="48px"
          px="26px"
          borderRadius="12px"
          fontWeight="700"
          flexShrink={0}
          boxShadow="0 8px 18px rgba(229,31,68,0.22)"
          _hover={{ bg: "#c41a3a" }}
          _active={{ bg: "#a8152f" }}
        >
          Zapisz się
        </Button>
      </Flex>
      {!!model.emailError && (
        <Text variant="error" pt="6px" fontSize="13px">
          {model.emailError}
        </Text>
      )}

      <Divider my="22px" borderColor="rgba(67,0,152,0.08)" />

      <Checkbox isChecked={model.isAllConsents} onChange={model.checkAllConsents.bind(model)} colorScheme="purple" mb="12px">
        <Text fontSize="13px" fontWeight="700" color="primary">
          Zaznacz wszystkie zgody
        </Text>
      </Checkbox>

      <VStack align="stretch" spacing="11px">
        {model.consents.map(consent => (
          <Checkbox
            key={consent.id}
            isChecked={consent.isChecked}
            onChange={() => model.toggleConsent(consent.id)}
            colorScheme="purple"
            alignItems="flex-start"
            sx={{ ".chakra-checkbox__control": { marginTop: "2px" } }}
          >
            <CustomTooltip message={consent.content.includes("<a") ? NEW_TAB_WARNING : ""}>
              <Text fontSize="12.5px" color="gray.600" lineHeight="1.55" display="inline">
                {convertHtmlToText(consent.content)}
                {consent.isRequired && (
                  <Text as="span" color="accent" fontWeight="700">
                    {" "}
                    *
                  </Text>
                )}
              </Text>
            </CustomTooltip>
          </Checkbox>
        ))}
      </VStack>
      {model.consentsError && (
        <Text variant="error" pt="8px" fontSize="13px">
          {convertHtmlToText(model.consentsError)}
        </Text>
      )}
    </Box>
  );
});
